export const MODES = {
  radice: "√ giorni",
  giornaliero: "× giorni",
  fisso: "Fisso",
  ledwall: "+25%/gg",
};

export const MODE_HINT = {
  radice: "Prezzo giornaliero × √giorni (attrezzature)",
  giornaliero: "Prezzo giornaliero × giorni (tecnici)",
  fisso: "Prezzo unico (trasporti, cablaggi)",
  ledwall: "Primo giorno + 25% del primo giorno per ogni giorno in più (ledwall)",
};

const r2 = (x) => Math.round((x + 1e-9) * 100) / 100;

export function factor(mode, days) {
  const d = Math.max(parseInt(days || 1, 10) || 1, 1);
  if (mode === "radice") return Math.sqrt(d);
  if (mode === "giornaliero") return d;
  if (mode === "ledwall") return 1 + 0.25 * (d - 1);
  return 1;
}

export function lineTotal(l, quoteDays) {
  if (l.omaggio) return 0;
  return r2((+l.qty || 0) * (+l.price || 0) * factor(l.mode, l.days || quoteDays));
}

export function lineCost(l, quoteDays) {
  return r2((+l.qty || 0) * (+l.cost || 0) * factor(l.cost_mode || l.mode, l.days || quoteDays));
}

export const marginTone = (pct) => (pct >= 70 ? "green" : pct >= 55 ? "yellow" : "red");

export const SERVICE_SECTIONS = ["RISORSE UMANE", "ALTRO", "ASSISTENZA", "ALLESTIMENTO E SMONTAGGIO", "LOGISTICA"];

export const vanFuel = (v, q) => {
  const km = +q.distance_km || 0, cons = +v.consumption || 0, fuel = +q.fuel_price || 0;
  return km > 0 && cons > 0 ? r2((2 * km / cons) * fuel) : 0;
};

export function transportTotals(q) {
  const days = Math.max(parseInt(q.days || 1, 10) || 1, 1);
  const vans = q.vans || [];
  const noleggio = r2(vans.reduce((s, v) => s + (+v.price || 0) * days, 0));
  const gasolio = r2(vans.reduce((s, v) => s + vanFuel(v, q), 0));
  const pedaggi = r2(vans.reduce((s, v) => s + (+v.toll || 0), 0));
  const costo = r2(vans.reduce((s, v) => s + (+v.cost || 0) * days, 0) + gasolio + pedaggi);
  return { noleggio, gasolio, pedaggi, totale: r2(noleggio + gasolio + pedaggi), costo };
}

export const MEAL_PRICE = 25;
const SETUP_RE = /allestiment|giorno precedente/i;
const slotKind = (n) => (SETUP_RE.test(n) ? "setup" : /smontaggi/i.test(n) ? "teardown" : "event");
export const techSlots = (q) => (q.lines || []).filter((l) => l.category === "RISORSE UMANE" && !l.omaggio)
  .flatMap((l) => Array.from({ length: Math.max(Math.round(+l.qty || 0), 0) }, () => slotKind(l.name || "")))
  .map((kind, i) => ({ label: `Tecnico ${i + 1}`, kind }));
export const slotDays = (q, t) => (t.kind !== "event" ? 1 : Math.max(parseInt(q.days || 1, 10) || 1, 1));
const mealDefaults = { lunch: MEAL_PRICE, dinner: MEAL_PRICE };
export const setMealDefaults = (s) => { mealDefaults.lunch = s.default_lunch_price ?? MEAL_PRICE; mealDefaults.dinner = s.default_dinner_price ?? MEAL_PRICE; };
export const mealAt = (q, i) => ({ lunch: false, dinner: false, lunch_price: mealDefaults.lunch, dinner_price: mealDefaults.dinner, skip_lunch: [], skip_dinner: [], ...((q.meals || [])[i] || {}) });
const mealCount = (m, key, days) => (m[key] ? days - new Set((m[`skip_${key}`] || []).filter((d) => d >= 0 && d < days)).size : 0);
export const mealSum = (m, days) => mealCount(m, "lunch", days) * (+m.lunch_price || 0) + mealCount(m, "dinner", days) * (+m.dinner_price || 0);
export function mealTotals(q) {
  const days = Math.max(parseInt(q.days || 1, 10) || 1, 1);
  return r2(techSlots(q).reduce((s, t, i) => s + mealSum(mealAt(q, i), slotDays(q, t)), 0));
}

export const CABLING = "Cablaggi, convertitori e canalizzazioni";
export const cablingPct = (q) => (q.cabling_pct === undefined || q.cabling_pct === null ? 10 : +q.cabling_pct || 0);

export function quoteTotals(q) {
  const ls = q.lines || [];
  const tr = transportTotals(q);
  const pasti = mealTotals(q);
  const baseMat = r2(ls.filter((l) => !SERVICE_SECTIONS.includes(l.category)).reduce((s, l) => s + lineTotal(l, q.days), 0));
  const cablaggi = r2((baseMat * cablingPct(q)) / 100);
  const materiale = r2(baseMat + cablaggi);
  const diritto = q.fixed_fee_on ? r2(+q.fixed_fee || 0) : 0;
  const somma = r2(ls.reduce((s, l) => s + lineTotal(l, q.days), 0) + cablaggi + tr.totale + pasti + diritto);
  const scontoMat = r2((materiale * (+q.material_discount || 0)) / 100);
  const base = r2(somma - scontoMat);
  const dv = +q.discount_value || 0;
  const sconto = q.discount_type === "amount" ? r2(dv) : r2((base * dv) / 100);
  const totale = Math.max(r2(base - sconto), 0);
  const costi = r2(ls.reduce((s, l) => s + lineCost(l, q.days), 0) + tr.costo + pasti);
  const margine = r2(totale - costi);
  return { somma, materiale, scontoMat, sconto, totale, costi, margine, trasporto: tr.totale, pasti, cablaggi, diritto, baseMat, marginePct: totale ? r2((margine / totale) * 100) : 0 };
}

const fmt = new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", useGrouping: "always" });
export const eur = (v) => fmt.format(v || 0);

export const itDate = (s) => {
  if (!s) return "";
  const [y, m, d] = s.slice(0, 10).split("-");
  return d ? `${d}/${m}/${y}` : s;
};

export const newLineId = () =>
  (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

export const lineFromItem = (it, qty = 1) => ({
  id: newLineId(),
  item_id: it.id,
  category: it.category,
  name: it.name,
  details: it.details || "",
  qty,
  days: null,
  price: it.price,
  mode: it.mode,
  omaggio: false,
  value: 0,
  cost: it.cost || 0,
  cost_mode: it.cost_mode || it.mode,
  source: it.source || "proprio",
});
